'use client';

import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { useListingCTAs } from '@/src/components/features/property/hooks/useListingCTAs';
import { Button } from '@zillow/constellation-10';

type Props = {
  listing: PropertyDetailListing;
  testIdSuffix?: string;
};

const FOOTER_CTA_TRIGGER_LOCATION = 'home_details_halo';
const FOOTER_CTA_TRIGGER_OBJECT = 'home_details_component|modal_contact_form';

/**
 * Mobile sticky footer CTAs — Contact / Tour / Apply buttons rendered
 * inside ListingPage's `spokeFooterCTAs` slot on mobile.
 *
 * Reversed order matches legacy: primary action (Contact) appears on the
 * right. Uses `[...ctas].reverse()` rather than `ctas.toReversed()` for
 * Safari 15 compatibility.
 */
export const SpokeFooterCTAs = ({ listing, testIdSuffix }: Props) => {
  const { ctas, overlays } = useListingCTAs(listing, FOOTER_CTA_TRIGGER_LOCATION, FOOTER_CTA_TRIGGER_OBJECT);
  const getTestId = (testId: string) => (testIdSuffix ? `${testId}-${testIdSuffix}` : testId);

  if (ctas.length === 0) return null;

  return (
    <>
      {[...ctas].reverse().map((cta) => (
        <Button
          key={cta.id}
          data-testid={getTestId(`button-${cta.id === 'contact' ? 'message' : cta.id}`)}
          tone="brand"
          emphasis={cta.emphasis}
          fluid
          icon={cta.icon}
          onClick={cta.onClick}
          disabled={cta.disabled}
          aria-busy={cta.ariaBusy}
          aria-label={cta.ariaLabel}
        >
          {cta.label}
        </Button>
      ))}
      {overlays}
    </>
  );
};
